import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtDateTime, groupIban, transferTypeLabel } from "@/lib/format";
import { smartSaveOrShare } from "@/lib/download";
import { usePdfViewer } from "@/lib/PdfViewerContext";
import { ReceiptPreview } from "@/components/DocumentPreview";
import { NoteModal, TxAlertModal } from "@/components/ReceiptEditModals";
import { toast } from "sonner";
import { Check, X, ShieldAlert, RefreshCw, Filter, Download, Package, MessageSquareWarning, Save, AlertTriangle, StickyNote, RotateCcw, Search, Eye, Loader2 } from "lucide-react";

const STATUS_TABS = [
  { key: "pending",   labelKey: "approvals.tab_pending",   fallback: "Pendientes",  badge: "bg-orange-100 text-orange-700" },
  { key: "scheduled", labelKey: "approvals.tab_scheduled", fallback: "Programadas", badge: "bg-blue-100 text-blue-700" },
  { key: "completed", labelKey: "approvals.tab_approved",  fallback: "Aprobadas",   badge: "bg-emerald-100 text-emerald-700" },
  { key: "rejected",  labelKey: "approvals.tab_rejected",  fallback: "Rechazadas",  badge: "bg-red-100 text-red-700" },
  { key: "refunded",  labelKey: "approvals.tab_refunded",  fallback: "Reembolsadas", badge: "bg-purple-100 text-purple-700" },
  { key: "cancelled", labelKey: "approvals.tab_cancelled", fallback: "Canceladas",  badge: "bg-slate-200 text-slate-700" },
];

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const cls = {
    pending: "bg-orange-100 text-orange-700",
    completed: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700",
    scheduled: "bg-blue-100 text-blue-700",
    refunded: "bg-purple-100 text-purple-700",
    cancelled: "bg-slate-200 text-slate-700",
  }[status] || "bg-slate-100 text-slate-700";
  const labelKey = {
    pending: "approvals.status_pending", completed: "approvals.status_approved",
    rejected: "approvals.status_rejected", scheduled: "approvals.status_scheduled",
    refunded: "approvals.status_refunded", cancelled: "approvals.status_cancelled",
  }[status];
  const fb = { cancelled: "Cancelada" }[status];
  return <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-mono-fin font-semibold ${cls}`}>{labelKey ? t(labelKey, { defaultValue: fb || status }) : status}</span>;
}

function RejectModal({ tx, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  if (!tx) return null;
  const submit = async () => {
    setLoading(true);
    await onConfirm(tx, reason.trim());
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
         onClick={onClose} data-testid="reject-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center"><X className="text-red-600" size={20} /></div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-xl text-slate-900">{t("approvals.reject_title")}</h3>
            <p className="text-sm text-slate-500 mt-1">
              {tx.reference} · {fmtMoney(Math.abs(tx.amount), tx.currency)} · {tx.beneficiary_name}
            </p>
          </div>
        </div>
        <label className="block mt-5">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("approvals.reject_label")}</span>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            rows={3} data-testid="reject-reason"
            placeholder={t("approvals.reject_placeholder")}
            className="w-full mt-2 px-3 py-3 rounded-xl border border-slate-200 outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 text-sm"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm" data-testid="reject-cancel">{t("approvals.cancel")}</button>
          <button onClick={submit} disabled={loading}
                  data-testid="reject-confirm"
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm">
            {loading ? "…" : <><X size={16} /> {t("approvals.reject_confirm")}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApproveModal({ tx, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [includeAlert, setIncludeAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  if (!tx) return null;
  const submit = async () => {
    setLoading(true);
    await onConfirm(tx, includeAlert);
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
         onClick={onClose} data-testid="approve-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><Check className="text-emerald-600" size={20} /></div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-xl text-slate-900">{t("approvals.approve_title")}</h3>
            <p className="text-sm text-slate-500 mt-1">
              {tx.reference} · {fmtMoney(Math.abs(tx.amount), tx.currency)} · {tx.beneficiary_name}
            </p>
          </div>
        </div>

        <label className={`block mt-5 rounded-2xl border-2 p-4 cursor-pointer transition-colors ${includeAlert ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-amber-300"}`}>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={includeAlert}
              onChange={(e) => setIncludeAlert(e.target.checked)}
              data-testid="approve-include-alert"
              className="mt-1 w-4 h-4 accent-amber-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-semibold text-slate-900 text-sm">
                <AlertTriangle size={15} className="text-amber-500" /> {t("approvals.approve_include_alert")}
              </div>
            </div>
          </div>
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm" data-testid="approve-cancel">{t("approvals.cancel")}</button>
          <button onClick={submit} disabled={loading}
                  data-testid="approve-confirm"
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
            {loading ? t("approvals.approve_executing") : <><Check size={16} /> {t("approvals.approve_confirm")}</>}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function AdminApprovals() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { openPdf } = usePdfViewer();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [rejectTx, setRejectTx] = useState(null);
  const [noteTx, setNoteTx] = useState(null);
  const [approveTx, setApproveTx] = useState(null);
  const [alertTx, setAlertTx] = useState(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, refunded: 0 });

  const viewReceipt = (tx) => openPdf({
    title: `${t("documents.receipt_word", { defaultValue: "Comprobante" })} ${tx.reference || ""}`.trim(),
    content: <ReceiptPreview tx={tx} />,
    path: `/admin/documents/receipt/${tx.id}?lang=${(i18n.language || "es").split("-")[0]}`,
    filename: `comprobante_${(tx.reference || tx.id).replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`,
  });
  const downloadReceipt = async (tx) => {
    if (downloadingId) return;
    setDownloadingId(tx.id);
    const filename = `comprobante_${(tx.reference || tx.id).replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`;
    try { await smartSaveOrShare(`/admin/documents/receipt/${tx.id}?lang=${(i18n.language || "es").split("-")[0]}`, filename); }
    finally { setDownloadingId(null); }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get(`/admin/transfers?status=${tab}`),
        api.get("/admin/transfers/stats"),
      ]);
      setRows(list.data || []);
      setStats(st.data || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredRows = rows.filter((tx) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [tx.user_full_name, tx.user_email, tx.beneficiary_name, tx.reference, tx.destination]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/app" replace />;

  const approve = async (tx, includeAlert = false) => {
    setActing(tx.id);
    try {
      await api.post(`/admin/transfers/${tx.id}/approve`, { include_receipt_alert: includeAlert });
      toast.success(`${t("approvals.toast_approved")} · -${fmtMoney(Math.abs(tx.amount), tx.currency)}`);
      setApproveTx(null);
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setActing(null); }
  };

  const saveTxAlert = async (tx, { mode, title, message }) => {
    setActing(tx.id);
    try {
      await api.put(`/admin/transfers/${tx.id}/alert`, { mode, title, message });
      toast.success(t("approvals.tx_alert_saved", { defaultValue: "Aviso actualizado" }));
      setAlertTx(null);
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setActing(null); }
  };

  const doCancel = async (tx) => {
    setActing(tx.id);
    try {
      await api.post(`/admin/transfers/${tx.id}/cancel`);
      toast.success(t("approvals.cancel_success", { defaultValue: "Transferencia cancelada" }));
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setActing(null); }
  };

  const doReject = async (tx, reason) => {
    setActing(tx.id);
    try {
      await api.post(`/admin/transfers/${tx.id}/reject`, { reason });
      toast.success(t("approvals.toast_rejected"));
      setRejectTx(null);
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setActing(null); }
  };

  const saveNote = async (tx, note) => {
    setActing(tx.id);
    try {
      await api.post(`/admin/transfers/${tx.id}/note`, { note });
      toast.success(note ? t("approvals.toast_note_saved") : t("approvals.toast_note_deleted"));
      setNoteTx(null);
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setActing(null); }
  };

  const doRefund = async (tx) => {
    if (!window.confirm(t("approvals.refund_confirm", { amount: fmtMoney(Math.abs(tx.amount), tx.currency), who: tx.user_full_name || tx.user_email }))) return;
    setActing(tx.id);
    try {
      await api.post(`/admin/transfers/${tx.id}/refund`);
      toast.success(`${t("approvals.toast_refunded")} · +${fmtMoney(Math.abs(tx.amount), tx.currency)}`);
      await fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setActing(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-approvals-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-fin text-xs uppercase tracking-widest text-red-600">{t("approvals.eyebrow")}</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900 mt-1">
            {t("approvals.title")}
          </h1>
          <p className="text-slate-500 mt-1">{t("approvals.subtitle")}</p>
        </div>
        <button onClick={fetchAll} disabled={loading} className="btn-secondary text-sm" data-testid="refresh-btn">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> {t("approvals.refresh")}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          [t("approvals.stat_pending"), stats.pending, "from-orange-500 to-red-500", "pending-stat"],
          [t("approvals.stat_approved"),  stats.approved, "from-emerald-500 to-green-600", "approved-stat"],
          [t("approvals.stat_rejected"), stats.rejected, "from-rose-500 to-red-600", "rejected-stat"],
        ].map(([label, val, grad, tid]) => (
          <div key={tid} className={`rounded-2xl bg-gradient-to-br ${grad} text-white p-5 shadow-lg`}>
            <div className="text-[10px] font-mono-fin uppercase tracking-widest text-white/80">{label}</div>
            <div className="font-display font-black text-4xl mt-1" data-testid={tid}>{val}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="status-tabs">
        {STATUS_TABS.map((tabItem) => (
          <button
            key={tabItem.key}
            onClick={() => setTab(tabItem.key)}
            data-testid={`tab-${tabItem.key}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              tab === tabItem.key ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200 hover:border-slate-400"
            }`}
          >
            <span className={`inline-block mr-2 px-1.5 rounded-full text-[10px] ${tab === tabItem.key ? "bg-white/20 text-white" : tabItem.badge}`}>
              {{ pending: stats.pending, completed: stats.approved, rejected: stats.rejected, refunded: stats.refunded }[tabItem.key] ?? 0}
            </span>
            {t(tabItem.labelKey, { defaultValue: tabItem.fallback })}
          </button>
        ))}
      </div>

      {/* Search by client name */}
      <div className="relative max-w-md" data-testid="approvals-search">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="approvals-search-input"
          placeholder={t("approvals.search_placeholder")}
          className="w-full pl-11 pr-4 py-2.5 rounded-full border border-slate-200 bg-white outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 text-sm"
        />
        {q && (
          <button onClick={() => setQ("")} data-testid="approvals-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="approvals-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">
                <th className="px-5 py-3">{t("approvals.col_reference")}</th>
                <th className="px-5 py-3">{t("approvals.col_client")}</th>
                <th className="px-5 py-3">{t("approvals.col_type")}</th>
                <th className="px-5 py-3">{t("approvals.col_beneficiary")}</th>
                <th className="px-5 py-3 text-right">{t("approvals.col_amount")}</th>
                <th className="px-5 py-3">{t("approvals.col_date")}</th>
                <th className="px-5 py-3">{t("approvals.col_status")}</th>
                <th className="px-5 py-3 text-right">{t("approvals.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-500">{t("approvals.loading")}</td></tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-12 text-center">
                  <div className="inline-flex items-center gap-2 text-slate-500"><Filter size={16} /> {q ? t("approvals.search_empty") : t("approvals.empty")}</div>
                </td></tr>
              )}
              {!loading && filteredRows.map((tx) => (
                <tr key={tx.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`row-${tx.id}`}>
                  <td className="px-5 py-3 font-mono-fin text-xs">{tx.reference}</td>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-slate-900">{tx.user_full_name || "—"}</div>
                    <div className="text-[11px] font-mono-fin text-slate-500">{tx.user_email}</div>
                  </td>
                  <td className="px-5 py-3 text-slate-700">{transferTypeLabel(tx.transfer_type)}</td>
                  <td className="px-5 py-3">
                    <div className="text-slate-900">{tx.beneficiary_name}</div>
                    <div className="text-[11px] font-mono-fin text-slate-500">{groupIban(tx.destination)}</div>
                  </td>
                  <td className="px-5 py-3 text-right font-mono-fin font-bold text-red-600">{fmtMoney(Math.abs(tx.amount), tx.currency)}</td>
                  <td className="px-5 py-3 text-slate-600 text-xs">{fmtDateTime(tx.created_at)}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={tx.status} />
                    {tx.status === "rejected" && tx.rejection_reason && (
                      <div className="text-[11px] text-red-700 mt-1 max-w-[220px]">↳ {tx.rejection_reason}</div>
                    )}
                    {tx.reviewed_by && (
                      <div className="text-[10px] font-mono-fin text-slate-400 mt-1">{t("approvals.by", { who: tx.reviewed_by })}</div>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex flex-col items-end gap-1.5">
                      {tx.status === "pending" ? (
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => setApproveTx(tx)}
                            disabled={acting === tx.id}
                            data-testid={`approve-${tx.id}`}
                            className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-50"
                          >
                            <Check size={14} /> {t("approvals.btn_approve")}
                          </button>
                          <button
                            onClick={() => setRejectTx(tx)}
                            disabled={acting === tx.id}
                            data-testid={`reject-${tx.id}`}
                            className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-50"
                          >
                            <X size={14} /> {t("approvals.btn_reject")}
                          </button>
                        </div>
                      ) : null}
                      {tx.status === "completed" ? (
                        <button
                          onClick={() => doRefund(tx)}
                          disabled={acting === tx.id}
                          data-testid={`refund-${tx.id}`}
                          className="inline-flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-50"
                        >
                          <RotateCcw size={14} /> {t("approvals.btn_refund")}
                        </button>
                      ) : null}
                      {tx.status === "scheduled" ? (
                        <button
                          onClick={() => doCancel(tx)}
                          disabled={acting === tx.id}
                          data-testid={`cancel-${tx.id}`}
                          className="inline-flex items-center gap-1 bg-slate-700 hover:bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-50"
                        >
                          <X size={14} /> {t("approvals.btn_cancel", { defaultValue: "Cancelar" })}
                        </button>
                      ) : null}
                      <button
                        onClick={() => setNoteTx(tx)}
                        disabled={acting === tx.id}
                        data-testid={`note-${tx.id}`}
                        className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border disabled:opacity-50 ${
                          tx.admin_note
                            ? "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                            : "bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-700"
                        }`}
                      >
                        <StickyNote size={13} /> {tx.admin_note ? t("approvals.btn_edit_note") : t("approvals.btn_add_note")}
                      </button>
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => setAlertTx(tx)}
                          data-testid={`tx-alert-btn-${tx.id}`}
                          className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border ${tx.effective_alert?.show ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600" : "bg-white text-slate-600 border-slate-200 hover:border-amber-400 hover:text-amber-700"}`}
                          title={t("approvals.tx_alert_title", { defaultValue: "Aviso de esta transferencia" })}
                        >
                          <AlertTriangle size={13} /> {t("approvals.tx_alert_btn", { defaultValue: "Aviso" })}
                        </button>
                        <button
                          onClick={() => viewReceipt(tx)}
                          data-testid={`view-receipt-${tx.id}`}
                          className="inline-flex items-center gap-1 bg-white border border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full"
                        >
                          <Eye size={13} /> {t("approvals.btn_view_receipt", { defaultValue: "Ver" })}
                        </button>
                        <button
                          onClick={() => downloadReceipt(tx)}
                          disabled={downloadingId === tx.id}
                          data-testid={`download-receipt-${tx.id}`}
                          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-50"
                        >
                          {downloadingId === tx.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} PDF
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-start gap-3 border-l-4 border-purple-600 bg-purple-50 px-4 py-3 text-xs text-slate-700 rounded-r-xl">
        <ShieldAlert size={16} className="text-purple-600 mt-0.5 shrink-0" />
        <span dangerouslySetInnerHTML={{ __html: t("approvals.workflow") }} />
      </div>

      <SettingsPanel />

      <ReceiptAlertPanel />

      <SourceZipPanel />

      <RejectModal tx={rejectTx} onClose={() => setRejectTx(null)} onConfirm={doReject} />
      <ApproveModal tx={approveTx} onClose={() => setApproveTx(null)} onConfirm={approve} />
      {alertTx && <TxAlertModal tx={alertTx} onClose={() => setAlertTx(null)} onConfirm={saveTxAlert} />}
      {noteTx && <NoteModal tx={noteTx} onClose={() => setNoteTx(null)} onConfirm={saveNote} />}
    </div> );
}

function SettingsPanel() {
  const [settings, setSettings] = useState({
    kyc_banner_title: "",
    kyc_banner_message: "",
    kyc_banner_cta_text: "",
    kyc_banner_enabled: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/public");
      setSettings(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      toast.success("Alarma KYC actualizada");
      setSettings({
        kyc_banner_title: data.kyc_banner_title,
        kyc_banner_message: data.kyc_banner_message,
        kyc_banner_cta_text: data.kyc_banner_cta_text,
        kyc_banner_enabled: data.kyc_banner_enabled,
      });
    } catch (e) {
      toast.error("No se pudieron guardar los cambios");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/settings/reset");
      toast.info("Valores restaurados a los predeterminados");
      setSettings({
        kyc_banner_title: data.kyc_banner_title,
        kyc_banner_message: data.kyc_banner_message,
        kyc_banner_cta_text: data.kyc_banner_cta_text,
        kyc_banner_enabled: data.kyc_banner_enabled,
      });
    } catch { toast.error("No se pudo restaurar"); }
    finally { setSaving(false); }
  };

  const onField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  return (
    <div className="rounded-3xl bg-gradient-to-br from-orange-50 via-white to-amber-50 border border-orange-200 p-6" data-testid="settings-panel">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shrink-0">
          <MessageSquareWarning className="text-white" size={22} />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="font-mono-fin text-[10px] uppercase tracking-widest text-orange-600">Mensajes al cliente</div>
          <h3 className="font-display font-bold text-xl text-slate-900 mt-1">Alarma de verificación KYC</h3>
          <p className="text-sm text-slate-600 mt-1">
            Edita el texto que ven los clientes en el banner del dashboard mientras no esté verificado su KYC.
            Los cambios se aplican inmediatamente para todos los usuarios.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <label className="block">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Título</span>
          <input
            value={settings.kyc_banner_title}
            onChange={(e) => onField("kyc_banner_title", e.target.value)}
            data-testid="settings-banner-title"
            disabled={loading}
            className="w-full mt-2 px-3 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-100 text-sm"
            placeholder="Verificación requerida"
          />
        </label>

        <label className="block">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Texto del botón</span>
          <input
            value={settings.kyc_banner_cta_text}
            onChange={(e) => onField("kyc_banner_cta_text", e.target.value)}
            data-testid="settings-banner-cta"
            disabled={loading}
            className="w-full mt-2 px-3 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-100 text-sm"
            placeholder="Verificar identidad"
          />
        </label>

        <label className="block md:col-span-2">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Mensaje</span>
          <textarea
            value={settings.kyc_banner_message}
            onChange={(e) => onField("kyc_banner_message", e.target.value)}
            data-testid="settings-banner-message"
            disabled={loading}
            rows={2}
            className="w-full mt-2 px-3 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-100 text-sm"
            placeholder="Sube tu documento de identidad (anverso y reverso) para activar todos los servicios."
          />
        </label>

        <label className="flex items-center gap-3 md:col-span-2 select-none cursor-pointer">
          <input
            type="checkbox"
            checked={!!settings.kyc_banner_enabled}
            onChange={(e) => onField("kyc_banner_enabled", e.target.checked)}
            data-testid="settings-banner-enabled"
            className="w-4 h-4 accent-orange-600"
          />
          <span className="text-sm text-slate-700">Mostrar el banner a los clientes con KYC sin verificar</span>
        </label>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={reset}
          disabled={saving || loading}
          data-testid="settings-reset"
          className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2.5 rounded-full text-sm disabled:opacity-50"
        >
          Restaurar predeterminados
        </button>
        <button
          onClick={save}
          disabled={saving || loading}
          data-testid="settings-save"
          className="inline-flex items-center gap-2 bg-gradient-to-br from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50"
        >
          <Save size={16} /> {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>

      {/* Live preview of the banner */}
      <div className="mt-5 border-t border-orange-200 pt-4">
        <div className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500 mb-2">Vista previa</div>
        <div className="rounded-2xl border border-orange-300 bg-orange-50 p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <ShieldAlert className="text-orange-600 mt-0.5" size={20} />
            <div>
              <div className="font-semibold text-orange-900">{settings.kyc_banner_title || "—"}</div>
              <div className="text-sm text-orange-800">{settings.kyc_banner_message || "—"}</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 bg-orange-900 text-white px-4 py-2 rounded-full text-sm font-semibold">
            {settings.kyc_banner_cta_text || "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

function ReceiptAlertPanel() {
  const TEMPLATES = [
    {
      key: "custom",
      label: "Personalizado",
      title: "",
      message: "",
    },
    {
      key: "payment_error",
      label: "Error de pago",
      title: "Aviso · Error en la transferencia",
      message: "Ha sucedido un error con su transferencia. Por favor comuníquese al chat de soporte para resolver la incidencia.",
    },
    {
      key: "fx_pending",
      label: "Comisión / divisas pendiente",
      title: "Aviso · Comisión pendiente",
      message: "Pendiente por pago de intercambio de divisas. Por favor póngase en contacto con el chat de soporte para finalizar la operación.",
    },
    {
      key: "docs_required",
      label: "Documentación requerida",
      title: "Aviso · Documentación requerida",
      message: "Para procesar esta transferencia necesitamos documentación adicional. Contacte al chat de soporte y le indicaremos los pasos.",
    },
    {
      key: "insufficient_funds",
      label: "Saldo insuficiente",
      title: "Aviso · Saldo insuficiente",
      message: "Su cuenta no dispone de saldo suficiente para completar esta operación. Recargue su cuenta o contacte al chat de soporte.",
    },
    {
      key: "compliance_review",
      label: "Revisión de cumplimiento",
      title: "Aviso · Revisión de cumplimiento",
      message: "Su transferencia está en revisión por parte de nuestro equipo de cumplimiento (AMLD5/MiCA). Le notificaremos cuando finalice. Para más detalles use el chat de soporte.",
    },
  ];
  const [settings, setSettings] = useState({
    receipt_alert_enabled: true,
    receipt_alert_title: "",
    receipt_alert_message: "",
    receipt_alert_show_support_btn: true,
  });
  const [tplKey, setTplKey] = useState("custom");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/public");
      setSettings({
        receipt_alert_enabled: data.receipt_alert_enabled !== false,
        receipt_alert_title: data.receipt_alert_title || "",
        receipt_alert_message: data.receipt_alert_message || "",
        receipt_alert_show_support_btn: data.receipt_alert_show_support_btn !== false,
      });
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const applyTemplate = (key) => {
    setTplKey(key);
    const tpl = TEMPLATES.find((x) => x.key === key);
    if (!tpl || key === "custom") return;
    setSettings((s) => ({ ...s, receipt_alert_title: tpl.title, receipt_alert_message: tpl.message }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Aviso del comprobante actualizado");
    } catch {
      toast.error("No se pudieron guardar los cambios");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/settings/reset");
      setSettings({
        receipt_alert_enabled: data.receipt_alert_enabled !== false,
        receipt_alert_title: data.receipt_alert_title || "",
        receipt_alert_message: data.receipt_alert_message || "",
        receipt_alert_show_support_btn: data.receipt_alert_show_support_btn !== false,
      });
      setTplKey("custom");
      toast.info("Valores restaurados a los predeterminados");
    } catch { toast.error("No se pudo restaurar"); }
    finally { setSaving(false); }
  };

  const onField = (k, v) => {
    setSettings((s) => ({ ...s, [k]: v }));
    setTplKey("custom"); // user edited manually, so switch back to "Personalizado"
  };

  return (
    <div className="rounded-3xl bg-gradient-to-br from-amber-50 via-white to-yellow-50 border border-amber-200 p-6" data-testid="receipt-alert-panel">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center shrink-0">
          <AlertTriangle className="text-white" size={22} />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="font-mono-fin text-[10px] uppercase tracking-widest text-amber-600">Comprobantes &amp; PDF</div>
          <h3 className="font-display font-bold text-xl text-slate-900 mt-1">Aviso en comprobantes de transferencia</h3>
          <p className="text-sm text-slate-600 mt-1">
            Define el <b>texto plantilla</b> del aviso. Con el interruptor maestro activo, el aviso
            se muestra <b>en TODOS los comprobantes</b> por defecto. Puedes anularlo o personalizarlo
            <b> por cliente</b> (en su ficha de Usuarios) o <b>por transferencia</b> (botón "Aviso" en cada fila).
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <label className="block md:col-span-2">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Plantilla rápida</span>
          <select
            value={tplKey}
            onChange={(e) => applyTemplate(e.target.value)}
            data-testid="receipt-alert-template"
            disabled={loading}
            className="w-full mt-2 px-3 py-2.5 rounded-xl border border-slate-300 bg-white outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-100 text-sm"
          >
            {TEMPLATES.map((tpl) => (
              <option key={tpl.key} value={tpl.key}>{tpl.label}</option>
            ))}
          </select>
          <span className="block text-[11px] text-slate-500 mt-1.5">
            Elige una plantilla común o "Personalizado" para escribir tu propio mensaje.
          </span>
        </label>

        <label className="block">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Título del aviso</span>
          <input
            value={settings.receipt_alert_title}
            onChange={(e) => onField("receipt_alert_title", e.target.value)}
            data-testid="receipt-alert-title-input"
            disabled={loading}
            className="w-full mt-2 px-3 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-100 text-sm"
            placeholder="Aviso · Error en la transferencia"
          />
        </label>

        <label className="flex items-center gap-3 select-none cursor-pointer mt-1">
          <input
            type="checkbox"
            checked={!!settings.receipt_alert_show_support_btn}
            onChange={(e) => onField("receipt_alert_show_support_btn", e.target.checked)}
            data-testid="receipt-alert-showbtn"
            className="w-4 h-4 accent-amber-600"
          />
          <span className="text-sm text-slate-700">Mostrar botón "Chat de soporte" (abre Tawk.to)</span>
        </label>

        <label className="block md:col-span-2">
          <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Mensaje</span>
          <textarea
            value={settings.receipt_alert_message}
            onChange={(e) => onField("receipt_alert_message", e.target.value)}
            data-testid="receipt-alert-message-input"
            disabled={loading}
            rows={3}
            className="w-full mt-2 px-3 py-2.5 rounded-xl border border-slate-300 outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-100 text-sm"
            placeholder="Ha sucedido un error con su transferencia. Por favor comuníquese al chat de soporte para resolver la incidencia."
          />
        </label>

        <label className="flex items-center gap-3 md:col-span-2 select-none cursor-pointer">
          <input
            type="checkbox"
            checked={!!settings.receipt_alert_enabled}
            onChange={(e) => onField("receipt_alert_enabled", e.target.checked)}
            data-testid="receipt-alert-enabled"
            className="w-4 h-4 accent-amber-600"
          />
          <span className="text-sm text-slate-700">Mostrar este aviso en <b>todos</b> los comprobantes por defecto (interruptor maestro · se puede anular por cliente o por transferencia)</span>
        </label>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={reset}
          disabled={saving || loading}
          data-testid="receipt-alert-reset"
          className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2.5 rounded-full text-sm disabled:opacity-50"
        >
          Restaurar predeterminados
        </button>
        <button
          onClick={save}
          disabled={saving || loading}
          data-testid="receipt-alert-save"
          className="inline-flex items-center gap-2 bg-gradient-to-br from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50"
        >
          <Save size={16} /> {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>

      {/* Live preview */}
      <div className="mt-5 border-t border-amber-200 pt-4">
        <div className="text-[10px] font-mono-fin uppercase tracking-widest text-slate-500 mb-2">Vista previa</div>
        {settings.receipt_alert_enabled ? (
          <div className="rounded-2xl border-2 p-4 flex items-start gap-3"
               style={{ borderColor: "#F59E0B", background: "linear-gradient(135deg,#FFFBEB,#FEF3C7)" }}>
            <div className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center" style={{ background: "#F59E0B", color: "#fff" }}>
              <AlertTriangle size={20} />
            </div>
            <div className="text-[13px] leading-relaxed text-amber-900 flex-1">
              <div className="font-bold uppercase tracking-wider text-xs text-amber-800">{settings.receipt_alert_title || "—"}</div>
              <div className="mt-1 whitespace-pre-line">{settings.receipt_alert_message || "—"}</div>
              {settings.receipt_alert_show_support_btn && (
                <span className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-xs font-bold shadow"
                      style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)" }}>
                  💬 Chat de soporte
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-500 italic px-2">Interruptor maestro desactivado: el aviso no se incluirá en ningún comprobante, aunque se marque al aprobar.</div>
        )}
      </div>
    </div>
  );
}

function SourceZipPanel() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/source-zip/info");
      setInfo(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const { data } = await api.post("/admin/source-zip/rebuild");
      setInfo({ ...data, exists: true, modified_at: data.generated_at });
      toast.success(`ZIP regenerado · ${data.size_kb} KB`);
    } catch (e) {
      toast.error("No se pudo regenerar el ZIP");
    } finally { setRebuilding(false); }
  };

  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/downloads/openpayments-source.zip`;
  const lastModified = info?.modified_at ? new Date(info.modified_at).toLocaleString("es-ES") : "—";

  return (
    <div className="rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-indigo-200 p-6" data-testid="source-zip-panel">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shrink-0">
          <Package className="text-white" size={22} />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="font-mono-fin text-[10px] uppercase tracking-widest text-indigo-600">Descarga de código</div>
          <h3 className="font-display font-bold text-xl text-slate-900 mt-1">ZIP de la aplicación</h3>
          <p className="text-sm text-slate-600 mt-1">
            Genera el snapshot más reciente del código (backend + frontend + memoria) y descárgalo a tu PC.
          </p>
          <div className="text-xs text-slate-500 mt-2 font-mono-fin">
            {info?.exists
              ? <>Última versión: <b>{info.size_kb} KB</b> · generado {lastModified}</>
              : (loading ? "Cargando…" : "Aún no existe — púlsalo Regenerar para crear uno")}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={rebuild}
            disabled={rebuilding}
            data-testid="rebuild-zip-btn"
            className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2.5 rounded-full text-sm disabled:opacity-50"
          >
            <RefreshCw size={16} className={rebuilding ? "animate-spin" : ""} />
            {rebuilding ? "Generando..." : "Regenerar"}
          </button>
          <a
            href={info?.exists ? downloadUrl : "#"}
            onClick={(e) => {
              if (!info?.exists) { e.preventDefault(); toast.warning("Regenera el ZIP primero"); }
            }}
            target="_blank"
            rel="noopener noreferrer"
            download
            data-testid="download-zip-link"
            className={`inline-flex items-center gap-2 font-semibold px-4 py-2.5 rounded-full text-sm text-white ${
              info?.exists
                ? "bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                : "bg-slate-300 cursor-not-allowed"
            }`}
          >
            <Download size={16} /> Descargar ZIP
          </a>
        </div>
      </div>
      <div className="mt-3 text-[11px] text-slate-500">
        ⓘ Si tu navegador bloquea la descarga dentro del iframe, copia esta URL y pégala en una pestaña nueva:
        <code className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 font-mono-fin text-slate-700 break-all">{downloadUrl}</code>
      </div>
    </div>
  );
}
